import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { useToast } from '../contexts/ToastContext';
import { Address } from '../types';
import DeleteConfirmModal from '../components/admin/DeleteConfirmModal';
import './Addresses.css';

interface AddressFormData {
  firstName: string;
  lastName: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
  isDefault: boolean;
  addressType: 'Shipping' | 'Billing' | 'Both';
}

const Addresses: React.FC = () => {
  const { success, error: showError, warning } = useToast();
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [showForm, setShowForm] = useState<boolean>(false);
  const [editingAddress, setEditingAddress] = useState<Address | null>(null);
  const [saving, setSaving] = useState<boolean>(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
  const [deleting, setDeleting] = useState<boolean>(false);
  const [formData, setFormData] = useState<AddressFormData>({
    firstName: '',
    lastName: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'US',
    isDefault: false,
    addressType: 'Shipping'
  });

  const fetchAddresses = useCallback(async (): Promise<void> => {
    try {
      const response = await api.get<Address[]>('/addresses');
      setAddresses(response.data);
    } catch (error) {
      console.error('Failed to fetch addresses:', error);
      showError('Failed to load addresses');
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    fetchAddresses();
  }, [fetchAddresses]);

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!formData.firstName.trim() || !formData.lastName.trim() || !formData.addressLine1.trim() ||
        !formData.city.trim() || !formData.state.trim() || !formData.zipCode.trim() || !formData.country.trim()) {
      warning('Please fill in all required address fields.');
      return;
    }
    setSaving(true);
    try {
      if (editingAddress) {
        await api.put(`/addresses/${editingAddress.id}`, formData);
        success('Address updated successfully!');
      } else {
        await api.post('/addresses', formData);
        success('Address added successfully!');
      }
      
      setShowForm(false);
      setEditingAddress(null);
      resetForm();
      fetchAddresses();
    } catch (error: any) {
      showError(error.response?.data?.message || 'Failed to save address');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (address: Address): void => {
    setEditingAddress(address);
    setFormData({
      firstName: address.firstName,
      lastName: address.lastName,
      addressLine1: address.addressLine1,
      addressLine2: address.addressLine2 || '',
      city: address.city,
      state: address.state,
      zipCode: address.zipCode,
      country: address.country || 'US',
      isDefault: address.isDefault,
      addressType: address.addressType || 'Shipping'
    });
    setShowForm(true);
  };

  const handleDelete = async (): Promise<void> => {
    if (!confirmDeleteId) return;
    setDeleting(true);
    try {
      await api.delete(`/addresses/${confirmDeleteId}`);
      success('Address deleted successfully!');
      fetchAddresses();
    } catch (error: any) {
      showError(error.response?.data?.message || 'Failed to delete address');
    } finally {
      setDeleting(false);
      setConfirmDeleteId(null);
    }
  };

  const resetForm = (): void => {
    setFormData({
      firstName: '',
      lastName: '',
      addressLine1: '',
      addressLine2: '',
      city: '',
      state: '',
      zipCode: '',
      country: 'US',
      isDefault: false,
      addressType: 'Shipping'
    });
  };

  const handleCancel = (): void => {
    setShowForm(false);
    setEditingAddress(null);
    resetForm();
  };

  if (loading) {
    return <div className="loading">Loading addresses...</div>;
  }

  return (
    <div className="addresses-page">
      <div className="container">
        <div className="addresses-header">
          <h1 className="page-title">My Addresses</h1>
          <button
            onClick={() => {
              resetForm();
              setEditingAddress(null);
              setShowForm(true);
            }}
            className="btn btn-primary"
          >
            Add New Address
          </button>
        </div>

        {showForm && (
          <div className="address-form-card">
            <h2>{editingAddress ? 'Edit Address' : 'Add New Address'}</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label>First Name</label>
                  <input
                    type="text"
                    value={formData.firstName}
                    onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Last Name</label>
                  <input
                    type="text"
                    value={formData.lastName}
                    onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Address Line 1</label>
                <input
                  type="text"
                  value={formData.addressLine1}
                  onChange={(e) => setFormData({ ...formData, addressLine1: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label>Address Line 2 (Optional)</label>
                <input
                  type="text"
                  value={formData.addressLine2}
                  onChange={(e) => setFormData({ ...formData, addressLine2: e.target.value })}
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>City</label>
                  <input
                    type="text"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>State</label>
                  <input
                    type="text"
                    value={formData.state}
                    onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Zip Code</label>
                  <input
                    type="text"
                    value={formData.zipCode}
                    onChange={(e) => {
                      // Allow only digits and hyphens, limit length
                      const value = e.target.value.replace(/[^0-9-]/g, '').slice(0, 10);
                      setFormData({ ...formData, zipCode: value });
                    }}
                    onBlur={(e) => {
                      // Validate ZIP code format (US: 5 digits or 5+4 format)
                      const zip = e.target.value.trim();
                      if (zip && !/^\d{5}(-\d{4})?$/.test(zip)) {
                        warning('Please enter a valid ZIP code (e.g., 12345 or 12345-6789)');
                      }
                    }}
                    pattern="^\d{5}(-\d{4})?$"
                    placeholder="12345 or 12345-6789"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Country</label>
                  <input
                    type="text"
                    value={formData.country}
                    onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.isDefault}
                    onChange={(e) => setFormData({ ...formData, isDefault: e.target.checked })}
                  />
                  Set as default address
                </label>
              </div>

              <div className="form-actions">
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving && <span className="btn-spinner" aria-hidden="true"></span>}
                  {editingAddress ? 'Update Address' : 'Add Address'}
                </button>
                <button type="button" onClick={handleCancel} className="btn btn-secondary" disabled={saving}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {addresses.length === 0 && !showForm ? (
          <div className="no-addresses">
            <p>You haven't saved any addresses yet.</p>
            <button
              onClick={() => {
                resetForm();
                setShowForm(true);
              }}
              className="btn btn-primary"
            >
              Add Your First Address
            </button>
          </div>
        ) : (
          <div className="addresses-list">
            {addresses.map((address) => (
              <div key={address.id} className="address-card">
                <div className="address-header">
                  <h3>
                    {address.firstName} {address.lastName}
                    {address.isDefault && <span className="default-badge">Default</span>}
                  </h3>
                </div>
                <div className="address-details">
                  <p>{address.addressLine1}</p>
                  {address.addressLine2 && <p>{address.addressLine2}</p>}
                  <p>
                    {address.city}, {address.state} {address.zipCode}
                  </p>
                  <p>{address.country}</p>
                </div>
                <div className="address-actions">
                  <button
                    onClick={() => handleEdit(address)}
                    className="btn btn-secondary btn-small"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => setConfirmDeleteId(address.id)}
                    className="btn btn-danger btn-small"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <DeleteConfirmModal
        isOpen={confirmDeleteId !== null}
        onClose={() => setConfirmDeleteId(null)}
        onConfirm={handleDelete}
        loading={deleting}
        itemName={confirmDeleteId ? `${addresses.find(a => a.id === confirmDeleteId)?.addressLine1 || 'address'}` : undefined}
      />
    </div>
  );
};

export default Addresses;

